import type { FeedComment } from '@/types/feed.type'

import React, { useState } from 'react'
import { fDateTime } from '@/utils/format-date'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

import ReplyComment from './feed-comment-replay'
import MediaGallery from './feed-comment-media-gallery'

// Main Comment Component
interface MainCommentProps {
  comment: FeedComment
  replies: FeedComment[]
  onReply: (comment: FeedComment) => void
  onMediaClick: (media: {
    url: string
    type: 'image' | 'gif' | 'sticker' | 'video'
  }) => void
}

const MainComment: React.FC<MainCommentProps> = ({
  comment,
  replies,
  onReply,
  onMediaClick,
}) => {
  const [showReplies, setShowReplies] = useState(false)
  const { imageUrl, displayName, firstName, lastName, email, role } =
    comment.user
  const name =
    firstName && lastName ? `${firstName} ${lastName}` : displayName || email

  return (
    <div className="bg-white rounded-lg p-4 space-y-3 border border-gray-200 shadow-sm">
      <div className="flex items-start space-x-3">
        <Avatar>
          <AvatarImage
            className="object-cover"
            src={role !== 'ADMIN' ? imageUrl : '/images/rfc-logo.png'}
            alt={name}
          />
          {!!name && (
            <AvatarFallback className="bg-gray-100 text-gray-600">
              {role !== 'ADMIN' ? name.slice(0, 2) || '' : 'RFC'}
            </AvatarFallback>
          )}
        </Avatar>
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex flex-col items-start justify-between">
            <h4 className="text-sm font-medium text-gray-900">{name}</h4>
            <span className="text-xs text-gray-500">
              {fDateTime(comment.createdAt)}
            </span>
          </div>
          <p className="text-sm text-gray-700 break-all">{comment.content}</p>

          {/* Media Display with Gallery */}
          {comment.media && (
            <div className="mt-3">
              <MediaGallery
                media={[comment.media]}
                onMediaClick={onMediaClick}
              />
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center space-x-4 pt-2">
            <Button
              className="flex items-center space-x-1 text-xs text-primary-600 hover:text-primary-800 transition-colors border border-primary-200 rounded-sm px-2 py-1 hover:bg-primary-50"
              onClick={() => onReply(comment)}
              variant="outline"
            >
              ตอบกลับ
            </Button>
            <Button
              className="flex items-center space-x-1 text-xs text-gray-500 hover:text-primary-700 transition-colors"
              onClick={() => onReply(comment)}
              variant="ghost"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <span>{comment.replyCount || replies.length}</span>
            </Button>

            {/* Show/Hide replies Button */}
            {replies.length > 0 && (
              <Button
                className={`flex items-center space-x-1 text-xs text-secondary-600 hover:text-secondary-700 transition-colors`}
                onClick={() => setShowReplies(!showReplies)}
                variant="ghost"
              >
                <svg
                  className={`w-3 h-3 transition-transform ${showReplies ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                <span>
                  {showReplies
                    ? 'ซ่อนการตอบกลับ'
                    : `แสดงการตอบกลับ (${replies.length})`}
                </span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Replies Section */}
      {showReplies && replies.length > 0 && (
        <div className="ml-6 space-y-2 border-l-2 border-blue-200 pl-4">
          <div className="text-xs text-gray-500 mb-2">การตอบกลับ</div>
          {replies.map((reply) => (
            <ReplyComment
              key={reply.id}
              comment={reply}
              // onReply={onReply}
              onMediaClick={onMediaClick}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default MainComment
